import { createSelector } from "reselect"
import { chain, get, reduce, map, set, omit, isEmpty, pick, size, clone, filter, mapValues } from "lodash"
import { getGroupingOptions, getIsActive, getSelectedMetricType, getRawSort, getSelectedGroup, getSelectedPage, getSelectedPerformanceMetricType, getSelectedSubView } from "."
import { getTickerAndMicFromContributor, getIsNoLongerHeldFromContributor, isUnmodeled } from "helpers/securities"
import { getColumns } from "./columns"
import { assetDollarFormatter, FORMATS, makeCustomFormatter, percentageFormatter } from "helpers/formatters/numbers"
import { Creators as AssetAnalyticsCreators } from "../model"
import { getIsInvalidBetaInstrumentReason } from "modules/portfolio-root/selectors/beta-instrument"
import { buildContributorKey, extractContributorRisk } from "helpers/contributors"
import { METRIC_TYPES } from "helpers/assets/constants"
import { getResidualTooltipContents } from "helpers/assets/tooltips"
import { computeFactorSensitivityMap, computeGroupedFactorSensitivityMap } from "helpers/factorSensitivity"
import { computeMarginalContributionMap, computeGroupedMarginalContributionMap } from "helpers/marginalContribution"
import { computeGroupsMap, contributorsReducer, mergeContributorsMetrics, applySort, computeIdColumnPath, isCashOffsetContributor, extractContributorRiskFactor, computeAggregatedColumnTotals, getAssetsReviewColumnsByCategories, getFactorsFromContributor, extractContributorComposition } from "helpers/assets"
import { getSelectedView } from "selectors/assets"
import { getCategories } from "selectors/factors"
import { getSelectedViewIsContentSet } from "selectors/contentSets"

const ITEMS_PER_PAGE = 200
const EMPTY_ARRAY = []

const numberFormatter = makeCustomFormatter(FORMATS.NUMBER_FORMAT, { emptyValueFiller: "-" })

const getRawPositions = state => get(state, "global.data.assetAnalyticsComposition.data.model.simulation.composition.0.positions", EMPTY_ARRAY)
const getRawGroupedPositions = state => get(state, "global.data.assetAnalyticsGroupedComposition.data.model.simulation.composition.0.compositionBy", EMPTY_ARRAY)
const getRawPerformanceContributors = state => get(state, "global.data.assetAnalyticsPerformance.data.model.simulation.performanceContributors", EMPTY_ARRAY)
const getRawGroupedPerformanceContributors = state => get(state, "global.data.assetAnalyticsGroupedPerformance.data.model.simulation.groupedPerformanceContributors", EMPTY_ARRAY)
const getRawRiskContributors = state => get(state, "global.data.assetAnalyticsRisk.data.model.simulation.riskContributors", EMPTY_ARRAY)
const getRawGroupedRiskContributors = state => get(state, "global.data.assetAnalyticsGroupedRisk.data.model.simulation.groupedRiskContributors", EMPTY_ARRAY)
const getRawRiskAttribution = state => get(state, "global.data.assetAnalyticsRisk.data.model.simulation.risk.0.total")
const getRawBetaContributors = state => get(state, "global.data.assetAnalyticsBeta.data.model.simulation.betaContributors.0.contributors")
const getRawGroupedBetaContributors = state => get(state, "global.data.assetAnalyticsGroupedBeta.data.model.simulation.groupedBetaContributors.0.groupedContributors")
const getRawDynamicPredictedBetaContributors = state => get(state, "global.data.assetAnalyticsDynamicPredictedBeta.data.model.simulation.betaContributors.0.contributors")
const getRawGroupedDynamicPredictedBetaContributors = state => get(state, "global.data.assetAnalyticsDynamicPredictedGroupedBeta.data.model.simulation.groupedBetaContributors.0.groupedContributors")
const getRawComposition = state => get(state, "global.data.assetAnalyticsComposition.data.model.simulation.composition.0")
const getRawCompositionDollars = state => get(state, "global.data.assetAnalyticsComposition.data.model.compositionDollar.composition.0.positions", EMPTY_ARRAY)
const getRawGroupedCompositionDollars = state => get(state, "global.data.assetAnalyticsGroupedComposition.data.model.compositionDollar.composition.0.compositionBy", EMPTY_ARRAY)
const getRawExposureContributors = state => get(state, "global.data.assetAnalyticsExposure.data.model.simulation.exposureContributors.0.contributors", EMPTY_ARRAY)
const getRawGroupedExposureContributors = state => get(state, "global.data.assetAnalyticsGroupedExposure.data.model.simulation.groupedExposureContributors.0.groupedContributors")
const getRawRiskFactorsContributors = state => get(state, "global.data.assetAnalyticsRiskFactors.data.model.simulation.riskContributors")
const getRawGroupedRiskFactorsContributors = state => get(state, "global.data.assetAnalyticsGroupedRiskFactors.data.model.simulation.groupedRiskContributors")
const getRawFactorSensitivityContributors = state => get(state, "global.data.assetAnalyticsFactorSensitivity.data.model.simulation.factorSensitivityContributors", EMPTY_ARRAY)
const getRawGroupedFactorSensitivityContributors = state => get(state, "global.data.assetAnalyticsGroupedFactorSensitivity.data.model.simulation.groupedFactorSensitivityContributors", EMPTY_ARRAY)
const getRawMarginalContributionContributors = state => get(state, "global.data.assetAnalyticsMarginalContribution.data.model.simulation.riskContributors", EMPTY_ARRAY)
const getRawGroupedMarginalContributionContributors = state => get(state, "global.data.assetAnalyticsGroupedMarginalContribution.data.model.simulation.groupedRiskContributors", EMPTY_ARRAY)
export const getCompositionStatus = state => get(state, "global.data.assetAnalyticsComposition.status")
const getShowNotHeld = (_, props) => get(props, "showNotHeld")
const getSegment = (_, props) => get(props, "segment")
export const getStartDate = (_, props) => get(props, "startDate")
export const getEndDate = (_, props) => get(props, "endDate")

const sumEconomicExposureReducer = (acc, contributor) => {
  const key = buildContributorKey(contributor)
  if (acc[key]) {
    acc[key] += contributor.economicExposure
  } else {
    acc[key] = clone(contributor.economicExposure)
  }
  return acc
}

const getEconomicExposuresMap = createSelector(
  [getRawCompositionDollars],
  (positions) => chain(positions)
    .reduce(contributorsReducer, {})
    .mapValues(item => item.economicExposure)
    .value(),
)

const getGroupedEconomicExposuresMap = createSelector(
  [getRawGroupedCompositionDollars],
  (positions) =>
    reduce(positions, (acc, { id, economicExposure }) => set(acc, id, economicExposure), {}),
)

const getGroupedEconomicExposuresContributorsMap = createSelector(
  [getRawGroupedCompositionDollars],
  (positions) => chain(positions)
    .keyBy("id")
    .mapValues(group => chain(group)
      .get("contributors")
      .reduce(sumEconomicExposureReducer, {})
      .value())
    .value(),
)

const getContributorId = (contributor) => !isEmpty(contributor.id) ? contributor.id : "Unknown"

const extractContributorBeta = (contributor) => ({
  beta: {
    ...pick(contributor, ["predicted", "historical"]),
    standalone: get(contributor, "securityBeta"),
  },
})

const extractContributorDynamicPredictedBeta = (contributor) => ({
  beta: {
    ...pick(contributor, ["dynamicPredicted"]),
    standalone: get(contributor, "securityBeta"),
  },
})

const extractContributorExposure = (contributor) => ({
  exposure: reduce(contributor.factors, (acc, factor) => set(acc, factor.id, get(factor, "exposure")), {}),
})

const getCompositionMap = createSelector(
  [getRawPositions, getEconomicExposuresMap, getRawGroupedPositions, getGroupedEconomicExposuresMap, getGroupingOptions],
  (positions, expMap, groupedPositions, groupedExpMap, groupingOptions) => {
    const contributors = groupingOptions ? groupedPositions : positions
    const exposureMap = groupingOptions ? groupedExpMap : expMap

    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorComposition(contributor, exposureMap)), {})
  },
)

const getGroupedCompositionContributorsMap = createSelector(
  [getRawGroupedPositions, getGroupedEconomicExposuresContributorsMap],
  (groups, exposureMap) => computeGroupsMap(groups, { mapFn: extractContributorComposition, exposureMap }),
)

const extractPerformanceValue = (returns, metricType, residualReturns) => {
  const metricField = metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? "cumulativeCurrency" : "cumulativePercent"
  const value = get(returns, metricField)
  if (residualReturns != null) {
    const residualReturnsValue = get(residualReturns, metricField, 0)
    const residualFormatter = metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? assetDollarFormatter : percentageFormatter
    return {
      value,
      tooltipContents: getResidualTooltipContents(residualFormatter(residualReturnsValue)),
    }
  }
  return value
}

const extractContributorPerformance = (contributor, metricType) => {
  const attributionSummary = get(contributor, "attribution.summary")
  return {
    ...omit(contributor, ["attribution", "totalReturn"]),
    performance: {
      total: extractPerformanceValue(get(contributor, "totalReturn"), metricType, get(attributionSummary, "residualReturn")),
      factors: extractPerformanceValue(get(attributionSummary, "factorsReturn"), metricType),
      specific: extractPerformanceValue(get(attributionSummary, "specificReturn"), metricType),
      trading: extractPerformanceValue(get(attributionSummary, "tradingReturn"), metricType),
      residual: extractPerformanceValue(get(attributionSummary, "residualReturn"), metricType),
    },
  }
}

const makeExtractContributorPerformance = (metricType) => {
  return (contributor) => extractContributorPerformance(contributor, metricType)
}

const getGroupedPerformanceContributorsMap = createSelector(
  [getRawGroupedPerformanceContributors, getSelectedPerformanceMetricType],
  (groups, selectedMetricType) => computeGroupsMap(groups, { mapFn: makeExtractContributorPerformance(selectedMetricType) }),
)

const extractContributorPerformanceFactor = (contributor, metricType) => {
  return {
    ...pick(contributor, ["id", "assetClass", "modelProviderId"]),
    performanceFactors: getFactorsFromContributor(contributor, (factor) => extractPerformanceValue(get(factor, "return"), metricType)),
  }
}

const makeExtractContributorPerformanceFactor = (metricType, selectedSubView) => {
  return (contributor) => extractContributorPerformanceFactor(contributor, metricType, selectedSubView)
}

const getGroupedPerformanceFactorsContributorsMap = createSelector(
  [getRawGroupedPerformanceContributors, getSelectedPerformanceMetricType],
  (groups, selectedMetricType) => computeGroupsMap(groups, { mapFn: makeExtractContributorPerformanceFactor(selectedMetricType) }),
)

const getPerformanceFactorsMap = createSelector(
  [getRawPerformanceContributors, getRawGroupedPerformanceContributors, getGroupingOptions, getSelectedPerformanceMetricType],
  (performanceContributors, groupedPerformanceFactorsContributors, groupingOptions, selectedMetricType) => {
    const contributors = !groupingOptions ? performanceContributors : groupedPerformanceFactorsContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorPerformanceFactor(contributor, selectedMetricType)), {})
  },
)

const getRiskMap = createSelector(
  [getRawRiskContributors, getRawGroupedRiskContributors, getGroupingOptions],
  (riskContributors, groupedRiskContributors, groupingOptions) => {
    const contributors = !groupingOptions ? riskContributors : groupedRiskContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorRisk(contributor)), {})
  },
)

const getGroupedRiskContributorsMap = createSelector(
  [getRawGroupedRiskContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorRisk }),
)

const getBetaMap = createSelector(
  [getRawBetaContributors, getRawGroupedBetaContributors, getGroupingOptions],
  (betaContributors, groupedBetaContributors, groupingOptions) => {
    const contributors = !groupingOptions ? betaContributors : groupedBetaContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorBeta(contributor)), {})
  },
)

const getGroupedBetaContributorsMap = createSelector(
  [getRawGroupedBetaContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorBeta }),
)

const getDynamicPredictedBetaMap = createSelector(
  [getRawDynamicPredictedBetaContributors, getRawGroupedDynamicPredictedBetaContributors, getGroupingOptions],
  (betaContributors, groupedBetaContributors, groupingOptions) => {
    const contributors = !groupingOptions ? betaContributors : groupedBetaContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorDynamicPredictedBeta(contributor)), {})
  },
)

const getGroupedDynamicPredictedBetaContributorsMap = createSelector(
  [getRawGroupedDynamicPredictedBetaContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorDynamicPredictedBeta }),
)

const getExposureMap = createSelector(
  [getRawExposureContributors, getRawGroupedExposureContributors, getGroupingOptions],
  (exposureContributors, groupedExposureContributors, groupingOptions) => {
    const contributors = !groupingOptions ? exposureContributors : groupedExposureContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorExposure(contributor)), {})
  },
)

const getGroupedExposureContributorsMap = createSelector(
  [getRawGroupedExposureContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorExposure }),
)

const getRiskFactorsMap = createSelector(
  [getRawRiskFactorsContributors, getRawGroupedRiskFactorsContributors, getGroupingOptions, getSelectedSubView],
  (riskFactorsContributors, groupedRiskFactorsContributors, groupingOptions, selectedSubView) => {
    const contributors = !groupingOptions ? riskFactorsContributors : groupedRiskFactorsContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorRiskFactor(contributor, selectedSubView)), {})
  },
)

const getGroupedRiskFactorsContributorsMap = createSelector(
  [getRawGroupedRiskFactorsContributors, getSelectedSubView],
  (groups, selectedSubView) => computeGroupsMap(groups, { mapFn: (contributor) => extractContributorRiskFactor(contributor, selectedSubView) }),
)

const getFactorSensitivityMap = createSelector(
  [getRawFactorSensitivityContributors, getRawGroupedFactorSensitivityContributors, getGroupingOptions],
  computeFactorSensitivityMap,
)

const getGroupedFactorSensitivityMap = createSelector(
  [getRawGroupedFactorSensitivityContributors],
  computeGroupedFactorSensitivityMap,
)

const getMarginalContributionMap = createSelector(
  [getRawMarginalContributionContributors, getRawGroupedMarginalContributionContributors, getGroupingOptions],
  computeMarginalContributionMap,
)

const getGroupedMarginalContributionMap = createSelector(
  [getRawGroupedMarginalContributionContributors],
  computeGroupedMarginalContributionMap,
)

export const getReferenceEquity = createSelector(
  [getRawComposition],
  (composition) => get(composition, "referenceEquity", get(composition, "gmv")),
)

const computeContributorData = (contributor) => ({
  id: getContributorId(contributor),
  ...getTickerAndMicFromContributor(contributor),
  ...omit(contributor, ["id"]),
  isUnmodeled: isUnmodeled(contributor),
  isNoLongerHeld: getIsNoLongerHeldFromContributor(contributor),
  isNotHeld: get(contributor, "notHeld"),
})

const computeGroupData = (item) => ({
  ...item,
  label: item.name,
  isGroup: true,
})

const getPerformanceMap = createSelector(
  [getRawPerformanceContributors, getRawGroupedPerformanceContributors, getGroupingOptions, getSelectedPerformanceMetricType],
  (performanceContributors, groupedPerformanceContributors, groupingOptions, selectedMetricType) => {
    const contributors = groupingOptions ? groupedPerformanceContributors : performanceContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorPerformance(contributor, selectedMetricType)), {})
  },
)

export const getDataMap = createSelector(
  [getCompositionMap, getPerformanceMap, getRiskMap, getBetaMap, getExposureMap, getRiskFactorsMap, getPerformanceFactorsMap, getDynamicPredictedBetaMap, getFactorSensitivityMap, getMarginalContributionMap],
  mergeContributorsMetrics,
)

const getGroupContributorsDataMap = createSelector(
  [getGroupedCompositionContributorsMap, getGroupedPerformanceContributorsMap, getGroupedPerformanceFactorsContributorsMap, getGroupedRiskContributorsMap, getGroupedBetaContributorsMap, getGroupedExposureContributorsMap, getGroupedRiskFactorsContributorsMap, getGroupedDynamicPredictedBetaContributorsMap, getGroupedFactorSensitivityMap, getGroupedMarginalContributionMap],
  (composition, performance, performanceFactors, risk, beta, exposure, riskFactors, dynamicPredictedBeta, factorSensitivity, marginalContribution) => {
    return mergeContributorsMetrics(composition, performance, performanceFactors, risk, beta, exposure, riskFactors, dynamicPredictedBeta, factorSensitivity, marginalContribution)
  },
)

const computeGroupAggregatedColumns = (label, group, columns) => reduce(columns, (acc, { path }) =>
  set(acc, path, get(group, path)), {
  id: label,
  isAggregate: true,
})

const getCurrentData = createSelector(
  [getDataMap, getGroupContributorsDataMap, getSelectedGroup, getGroupingOptions, getShowNotHeld, getIsActive, getColumns, getSelectedMetricType],
  (data, groupContributorsData, selectedGroup, groupingOptions, showNotHeld, isActive, columns, metricType) => {
    if (groupingOptions && !selectedGroup) {
      return map(data, computeGroupData)
    }
    if (selectedGroup) {
      data = groupContributorsData[selectedGroup]
    }

    const contributors = chain(data)
      .filter(contributor => showNotHeld || !contributor.notHeld)
      .map(computeContributorData)
      .value()

    if (!showNotHeld && isActive) {
      const notHeldContributors = chain(data)
        .filter(contributor => contributor.notHeld)
        .map(computeContributorData)
        .value()
      if (size(notHeldContributors) > 0) {
        contributors.push({ ...computeAggregatedColumnTotals("NOT HELD", notHeldContributors, columns, metricType), isNotHeld: true })
      }
    }

    return contributors
  },
)

const getIdColumnPath = createSelector(
  [getGroupingOptions, getSelectedGroup],
  computeIdColumnPath,
)

export const getOrderedSecurities = createSelector(
  [getCurrentData, getRawSort, getColumns, getIdColumnPath],
  applySort,
)

export const getOrderedSecuritiesWithoutCashOffset = createSelector(
  [getOrderedSecurities],
  (securities) => filter(securities, security => !isCashOffsetContributor(security)),
)

export const getSecurities = createSelector(
  [getOrderedSecuritiesWithoutCashOffset, getSelectedPage],
  (orderedSecurities, selectedPage) => {
    const start = selectedPage * ITEMS_PER_PAGE
    return chain(orderedSecurities)
      .slice(start, start + ITEMS_PER_PAGE)
      .value()
  },
)

const getAvailableGroupsList = createSelector(
  [getCompositionMap, getPerformanceMap],
  (compositionGroups, performanceGroups) => mapValues(
    {
      ...performanceGroups,
      ...compositionGroups,
    },
    (group) => group.name,
  ),
)

export const getSelectedGroupName = createSelector(
  [getSelectedGroup, getAvailableGroupsList],
  (selectedGroup, groups) => get(groups, selectedGroup, selectedGroup),
)

export const getAggregatedData = createSelector(
  [getOrderedSecurities, getRawRiskAttribution, getColumns, getSelectedGroup, getSelectedGroupName, getSegment, getDataMap, getSelectedMetricType],
  (securities, riskAttribution, columns, selectedGroup, selectedGroupName, segment, dataMap, metricType) => {
    const isDrillDown = !!selectedGroup
    let aggregatedData
    if (isDrillDown) {
      aggregatedData = computeGroupAggregatedColumns(selectedGroupName, dataMap[selectedGroup], columns)
    } else {
      aggregatedData = computeAggregatedColumnTotals(segment ? "SEGMENT" : "TOTAL", securities, columns, metricType)
    }

    const totalRisk = get(aggregatedData, "risk.total")
    if (!isDrillDown && totalRisk != null) {
      aggregatedData.risk.total = {
        value: totalRisk,
        tooltipContents: [`Total Risk: ${numberFormatter(riskAttribution * 100)}%`],
      }
    }

    const residual = get(aggregatedData, "performance.residual")
    if (!isDrillDown && residual != null) {
      const residualFormatter = metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? assetDollarFormatter : percentageFormatter
      aggregatedData.performance.total = {
        value: aggregatedData.performance.total,
        tooltipContents: getResidualTooltipContents(residualFormatter(residual)),
      }
    }

    return {
      ...aggregatedData,
      isDrillDown,
      action: AssetAnalyticsCreators.setSelectedGroup,
    }
  },
)

export const getColumnsByCategories = createSelector(
  [getColumns, getCurrentData, getSelectedSubView, getSelectedView, getSelectedMetricType, getCategories, getIsInvalidBetaInstrumentReason, getSelectedViewIsContentSet],
  getAssetsReviewColumnsByCategories,
)

export const getQueries = createSelector(
  [getColumnsByCategories],
  (columnCategories) => {
    const columns = reduce(columnCategories, (acc, category) => [...acc, ...category.columns], [])
    const result = reduce(columns, (acc, column) => {
      acc[column.query] = true
      return acc
    }, {})
    return result
  },
)
